import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { Device } from '@capacitor/device';

// We dynamically import locale bundles so the initial JS payload contains
// ONLY the active language (saves ~4–5k lines of strings on first paint).
const loaders: Record<string, () => Promise<{ default: any }>> = {
  uk: () => import('./locales/uk'),
  en: () => import('./locales/en'),
  de: () => import('./locales/de'),
  es: () => import('./locales/es'),
  pl: () => import('./locales/pl'),
  fr: () => import('./locales/fr'),
  ro: () => import('./locales/ro'),
  bg: () => import('./locales/bg'),
};

export const SUPPORTED_LANGS = ['uk', 'en', 'de', 'es', 'pl', 'fr', 'ro', 'bg'] as const;
export type AppLanguage = typeof SUPPORTED_LANGS[number];
export const RELEASE_LANGS = ['uk', 'en', 'de', 'es', 'pl', 'fr', 'ro', 'bg'] as const;
export type ReleaseLanguage = typeof RELEASE_LANGS[number];
export const FALLBACK_LANG: AppLanguage = 'en';
const STORAGE_KEY = 'volty-lang';
const PREFERENCE_MODE_STORAGE_KEY = 'volty-lang-mode';
const MANUAL_PREFERENCE_MODE = 'manual';
const loaded = new Set<string>();
const NATIVE_LANGUAGE_TIMEOUT_MS = 250;

const LANGUAGE_ALIASES: Record<string, AppLanguage> = {
  ua: 'uk',
};

const safeGetStoredLanguage = () => {
  try { return localStorage.getItem(STORAGE_KEY); } catch { return null; }
};

const safeStoreLanguage = (lang: AppLanguage) => {
  try { localStorage.setItem(STORAGE_KEY, lang); } catch {}
};

const safeGetPreferenceMode = () => {
  try { return localStorage.getItem(PREFERENCE_MODE_STORAGE_KEY); } catch { return null; }
};

const safeStorePreferenceMode = () => {
  try { localStorage.setItem(PREFERENCE_MODE_STORAGE_KEY, MANUAL_PREFERENCE_MODE); } catch {}
};

const safeClearStoredLanguage = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(PREFERENCE_MODE_STORAGE_KEY);
  } catch {}
};

export function normalizeLanguage(value?: string | null): AppLanguage {
  return toSupportedLanguage(value) ?? FALLBACK_LANG;
}

function toSupportedLanguage(value?: string | null): AppLanguage | null {
  const base = (value || '').toLowerCase().split('-')[0];
  const mapped = LANGUAGE_ALIASES[base] || base;
  if ((SUPPORTED_LANGS as readonly string[]).includes(mapped)) {
    return mapped as AppLanguage;
  }
  return null;
}

export function resolveDeviceLanguage(languages: readonly string[] = []): AppLanguage {
  const primaryLanguage = languages.find((language) => Boolean(language?.trim()));
  const primaryBase = (primaryLanguage || '').toLowerCase().split('-')[0];

  // Russian is intentionally not a release locale. A device whose primary
  // language is Russian must open in English, even when Ukrainian appears as
  // a secondary browser language.
  if (primaryBase === 'ru') return FALLBACK_LANG;

  for (const language of languages) {
    const supported = toSupportedLanguage(language);
    if (supported) return supported;
  }

  return FALLBACK_LANG;
}

export const getSavedLanguage = () => {
  // Legacy builds saved the initial language but did not distinguish it from
  // an explicit choice. Treat that value as Auto once so a foreign device is
  // no longer pinned to an old Ukrainian default.
  if (safeGetPreferenceMode() !== MANUAL_PREFERENCE_MODE) return null;
  const saved = safeGetStoredLanguage();
  return saved ? toSupportedLanguage(saved) : null;
};

const getBrowserDeviceLanguages = (): string[] => {
  try {
    return navigator.languages?.length ? [...navigator.languages] : [navigator.language];
  } catch {
    return [];
  }
};

const getUrlLanguage = (): AppLanguage | null => {
  try {
    return toSupportedLanguage(new URLSearchParams(window.location.search).get('lang'));
  } catch {
    return null;
  }
};

export const getDeviceLanguage = () => resolveDeviceLanguage(getBrowserDeviceLanguages());

const getNativeDeviceLanguage = async (): Promise<string | null> => {
  if (!Capacitor.isNativePlatform()) return null;

  try {
    const nativeLanguage = Device.getLanguageTag()
      .then(({ value }) => value || null)
      .catch(() => null);
    const timeout = new Promise<null>((resolve) => {
      globalThis.setTimeout(() => resolve(null), NATIVE_LANGUAGE_TIMEOUT_MS);
    });
    return await Promise.race([nativeLanguage, timeout]);
  } catch {
    return null;
  }
};

export const getRuntimeDeviceLanguage = async (): Promise<AppLanguage> => {
  const nativeLanguage = await getNativeDeviceLanguage();
  return resolveDeviceLanguage([
    ...(nativeLanguage ? [nativeLanguage] : []),
    ...getBrowserDeviceLanguages(),
  ]);
};

export const getCurrentLanguage = () => normalizeLanguage(i18n.resolvedLanguage || i18n.language || FALLBACK_LANG);

export function resolveInitialLanguage({
  urlLanguage,
  savedLanguage,
  deviceLanguages,
}: {
  urlLanguage?: string | null;
  savedLanguage?: string | null;
  deviceLanguages?: readonly string[];
}): AppLanguage {
  return toSupportedLanguage(urlLanguage)
    ?? toSupportedLanguage(savedLanguage)
    ?? resolveDeviceLanguage(deviceLanguages);
}

async function getInitialLanguage(): Promise<AppLanguage> {
  const deviceLanguages = getBrowserDeviceLanguages();
  try {
    const nativeLanguage = await getNativeDeviceLanguage();
    return resolveInitialLanguage({
      urlLanguage: getUrlLanguage(),
      savedLanguage: getSavedLanguage(),
      deviceLanguages: [...(nativeLanguage ? [nativeLanguage] : []), ...deviceLanguages],
    });
  } catch {}
  return resolveDeviceLanguage(deviceLanguages);
}

async function ensureLoaded(lang: string) {
  const normalized = normalizeLanguage(lang);
  if (loaded.has(normalized) || !loaders[normalized]) return normalized;
  const mod = await loaders[normalized]();
  i18n.addResourceBundle(normalized, 'translation', mod.default, true, true);
  loaded.add(normalized);
  return normalized;
}

// Pre-load the initial language bundle BEFORE init so the first render
// already has translations — otherwise users see raw keys (`hero.title1`)
// for a few hundred ms while the dynamic import resolves.
export const i18nReady = (async () => {
  const initialLang = await getInitialLanguage();
  const mod = await loaders[initialLang]();
  const resources: Record<string, { translation: any }> = {
    [initialLang]: { translation: mod.default },
  };
  if (initialLang !== FALLBACK_LANG) {
    const fallbackMod = await loaders[FALLBACK_LANG]();
    resources[FALLBACK_LANG] = { translation: fallbackMod.default };
    loaded.add(FALLBACK_LANG);
  }
  await i18n.use(initReactI18next).init({
    resources,
    lng: initialLang,
    fallbackLng: FALLBACK_LANG,
    supportedLngs: [...SUPPORTED_LANGS],
    nonExplicitSupportedLngs: true,
    cleanCode: true,
    returnEmptyString: false,
    saveMissing: false,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
  });
  document.documentElement.lang = initialLang;
  loaded.add(initialLang);
})();


export const setLanguage = async (lang: string, options: { persist?: boolean } = {}) => {
  const normalized = await ensureLoaded(normalizeLanguage(lang));
  if (options.persist !== false) {
    safeStoreLanguage(normalized);
    safeStorePreferenceMode();
  }
  document.documentElement.lang = normalized;
  await i18n.changeLanguage(normalized);
};

export const resetLanguageToDevice = async () => {
  safeClearStoredLanguage();
  const deviceLanguage = await getRuntimeDeviceLanguage();
  await setLanguage(deviceLanguage, { persist: false });
  return deviceLanguage;
};

export const refreshAutoLanguageFromDevice = async () => {
  await i18nReady;
  const urlLanguage = getUrlLanguage();
  if (urlLanguage) {
    if (urlLanguage !== getCurrentLanguage()) {
      await setLanguage(urlLanguage, { persist: false });
    }
    return urlLanguage;
  }
  if (getSavedLanguage()) return getCurrentLanguage();

  const deviceLanguage = await getRuntimeDeviceLanguage();
  if (deviceLanguage !== getCurrentLanguage()) {
    await setLanguage(deviceLanguage, { persist: false });
  }
  return deviceLanguage;
};

export default i18n;
